import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Play, Users, Zap, Calendar, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { trackPurchase } from '../utils/analytics';

const WelcomePage = () => {
  const [sessionId, setSessionId] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get session ID from URL
    const params = new URLSearchParams(window.location.search);
    const sessionIdParam = params.get('session_id');
    
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
      // In a real app, you'd verify this session with your backend
      // For now, we'll simulate successful payment
      setTimeout(() => {
        const mockCustomerData = {
          name: 'New Customer',
          email: 'customer@example.com',
          plan: 'GROWTH'
        };
        setCustomerData(mockCustomerData);
        setIsLoading(false);
        
        // Track successful purchase
        trackPurchase({
          transactionId: sessionIdParam,
          plan: mockCustomerData.plan,
          currency: 'GBP',
          value: 497 // This should come from your backend in real implementation
        });
      }, 1000);
    } else {
      setIsLoading(false);
    }
  }, []);

  const quickStartSteps = [
    {
      icon: ExternalLink,
      title: 'Access Your Dashboard',
      description: 'Login to your Local Business Heroes dashboard and explore your new tools',
      action: 'Login Now',
      link: 'https://app.localbusinessheroes.net/'
    },
    {
      icon: Play,
      title: 'Watch Quick Start Video',
      description: '5-minute walkthrough to get you up and running immediately',
      action: 'Watch Video',
      link: '#'
    },
    {
      icon: Users,
      title: 'Connect Your Business Profile',
      description: 'Link your Google Business Profile and start capturing reviews automatically',
      action: 'Connect Now',
      link: '#'
    },
    {
      icon: Calendar,
      title: 'Book Your Strategy Call',
      description: 'FREE 30-minute call with our team to optimize your setup',
      action: 'Book Call',
      link: '#'
    }
  ];

  const trialBenefits = [
    '14 days full access to all features',
    'Unlimited lead capture and follow-up',
    'AI-powered automation setup',
    'Personal onboarding support',
    'Mobile app access',
    'Cancel anytime during trial'
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0E163B] to-[#1a2654] flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin w-12 h-12 border-4 border-white border-t-[#FF5A3D] rounded-full mx-auto mb-4"></div>
          <p className="text-lg">Processing your trial setup...</p>
        </div>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0E163B] to-[#1a2654] flex items-center justify-center text-white text-center px-4">
        <div className="max-w-md">
          <h1 className="text-3xl font-bold mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-6">This page is only accessible after a successful trial signup.</p>
          <Button asChild className="bg-[#FF5A3D] hover:bg-[#E5512E]">
            <a href="/pricing">Start Your Trial</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Success Header */}
      <section className="bg-gradient-to-r from-green-500 to-green-600 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-green-500" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Welcome to Local Business Heroes!
          </h1>
          
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Your 14-day trial is now active! Your business growth system is ready to transform your leads into loyal customers.
          </p>
          
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold mb-4">What happens next?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} />
                <span>Account created & activated</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} />
                <span>Templates imported</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} />
                <span>Welcome email sent</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} />
                <span>Support team notified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start Guide */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#FF5A3D]/10 text-[#FF5A3D] px-4 py-2 rounded-full mb-6">
              Quick Start Guide
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Get Up and Running in 4 Simple Steps
            </h2>
            <p className="text-xl text-gray-600">
              Follow these steps to start capturing and converting leads immediately
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {quickStartSteps.map((step, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#FF5A3D]/10 rounded-full flex items-center justify-center group-hover:bg-[#FF5A3D] group-hover:text-white transition-colors flex-shrink-0">
                      <step.icon size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-semibold text-[#0E163B]">{step.title}</h3>
                        <Badge className="bg-[#0E163B] text-white text-xs px-2 py-1">
                          Step {index + 1}
                        </Badge>
                      </div>
                      <p className="text-gray-600 mb-6">{step.description}</p>
                      <Button 
                        asChild
                        className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white w-full"
                      >
                        <a href={step.link} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2">
                          <span>{step.action}</span>
                          <ArrowRight size={16} />
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trial Details */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Your 14-Day Trial Includes:
              </h2>
              <div className="space-y-4">
                {trialBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle size={20} className="text-[#FF5A3D] flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
                <h3 className="font-semibold text-[#0E163B] mb-3">Pro Tip</h3>
                <p className="text-gray-600 text-sm">
                  The sooner you set up your lead capture pages and connect your business profiles, 
                  the faster you'll see results. Most customers see their first automated lead within 24 hours!
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Need Help? We're Here!</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#FF5A3D] rounded-full flex items-center justify-center">
                      <Users size={20} />
                    </div>
                    <div>
                      <div className="font-semibold">Live Chat Support</div>
                      <div className="text-sm text-gray-300">Available in your dashboard</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#FF5A3D] rounded-full flex items-center justify-center">
                      <Calendar size={20} />
                    </div>
                    <div>
                      <div className="font-semibold">Free Setup Call</div>
                      <div className="text-sm text-gray-300">Book your strategy session</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#FF5A3D] rounded-full flex items-center justify-center">
                      <Zap size={20} />
                    </div>
                    <div>
                      <div className="font-semibold">Video Tutorials</div>
                      <div className="text-sm text-gray-300">Step-by-step guidance</div>
                    </div>
                  </div>
                </div>
                
                <Button 
                  asChild
                  className="w-full mt-6 bg-white text-[#0E163B] hover:bg-gray-100"
                >
                  <a href="mailto:support@localbusinessheroes.net">
                    Contact Support
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Become a Local Legend?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Your success is our success. Let's turn your trial into explosive business growth!
          </p>
          
          <Button 
            asChild
            className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-xl px-10 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
          >
            <a href="https://app.localbusinessheroes.net/" target="_blank" rel="noopener noreferrer">
              Access Your Dashboard Now
            </a>
          </Button>
          
          <div className="flex flex-wrap justify-center gap-8 mt-8 text-sm opacity-75">
            <span>✅ No setup fees</span>
            <span>✅ Full feature access</span>
            <span>✅ Cancel anytime</span>
            <span>✅ Dedicated support</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WelcomePage;